import React, { useEffect, useMemo } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { Close } from '@emotion-icons/material/Close'
import styled from '@emotion/styled'
import { bbox, Feature, Polygon } from '@turf/turf'
import { GeoJSON2SVG } from 'geojson2svg'
import { Colors, Shapes, themeSpacing } from 'src/components/theme'
import genericMapBackdrop from 'src/assets/images/generic-map-backdrop.png'
import { UserListsPopover } from 'src/components/user-lists-popover'
import { IconButton } from '@mui/material'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { CheckCircle } from '@emotion-icons/fa-solid/CheckCircle'
import { Save } from '@emotion-icons/fa-solid/Save'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'

import * as atoms from 'src/atoms/custom-area'
import * as userListsAtoms from 'src/atoms/user-lists'
import * as userListsPopoverAtoms from 'src/atoms/user-lists/user-lists-popover'
import * as bordersAtoms from 'src/components/map-layers/borders-layers/atoms'
import * as mapToolsAtoms from 'src/atoms/main-map-tools'

export const fmt = (num: number | undefined, suffix: string): string => {
  return num ? `${num.toFixed(2)} ${suffix}` : '--'
}

const CloseButton = styled(IconButton)`
  position: absolute !important;
  top: ${themeSpacing(2)}px !important;
  right: ${themeSpacing(2)}px !important;
  background-color: ${Colors.bgWhite} !important;
  border: solid 1px ${Colors.border} !important;
  border-radius: 7px !important;
  display: flex !important;
  justify-content: flex-start !important;
  gap: ${themeSpacing(1)}px !important;
  z-index: 1 !important;
  color: ${Colors.textDark} !important;
  text-transform: capitalize !important;
  &:hover {
    background-color: ${Colors.neutral50} !important;
    border-color: ${Colors.border} !important;
  }
`

const Wrapper = styled.div`
  position: relative;
`

const Cover = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  background-image: url(${genericMapBackdrop.src});
  border-bottom-right-radius: ${Shapes.borderRadius}px;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0px;
    background-color: ${Colors.neutral950};
    opacity: 0.15;
    overflow: hidden;
  }

  .sidebar-collapse-button {
    position: absolute;
    right: 0px;
    top: 3px;
  }
`

const CustomAreaContainer = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 180px;
  z-index: 1;

  svg {
    height: 85%;

    path {
      stroke-width: 2px;
    }
  }
`

const StyledSaveButtonWrapper = styled.div`
  position: absolute;
  right: ${Shapes.spacing[4]}px;
  bottom: 0;
  transform: translateY(50%);
  z-index: 1;
`

const SaveButton = styled(ButtonWithIcon)`
  min-width: 80px;
`

export const CoverWithCloseButton: React.FC = () => {
  const [circle, setCircle] = useRecoilState(atoms.circle)
  const [poly, setPoly] = useRecoilState(atoms.polygon)
  const [userListItem, setUserListItem] = useRecoilState(userListsAtoms.userListItem)
  const setUserListsPopoverAnchorElement = useSetRecoilState(userListsPopoverAtoms.anchorElement)
  const savedIn = useRecoilValue(userListsAtoms.savedIn(userListItem))
  const isSaved = useRecoilValue(userListsAtoms.isSaved(userListItem))
  const setBordersPolygon = useSetRecoilState(bordersAtoms.polygon)
  const setDrawMode = useSetRecoilState(atoms.drawMode)
  const setSelectedTool = useSetRecoilState(mapToolsAtoms.mapPinsTools)
  const { clearSelected, selectArea } = useMapExplorerNavigation()

  useEffect(() => {
    if (circle) {
      setUserListItem({
        type: 'custom-area',
        data: {
          name: '',
          description: '',
          ...circle,
        },
      })
    }
    if (poly) {
      setUserListItem({
        type: 'custom-area',
        data: {
          name: '',
          description: '',
          ...poly,
        },
      })
    }
  }, [poly, circle])

  const onClose = async () => {
    setCircle(undefined)
    setPoly(undefined)
    setBordersPolygon(undefined)
    setDrawMode('none')
    setSelectedTool(null)
    selectArea(undefined) // for some reason, clearSelected is not clearing the geometry properly when url syncs, so I had to use selectArea to clear it
    await clearSelected()
  }

  const openUserListsPopover = (evt: React.MouseEvent<HTMLElement>) =>
    setUserListsPopoverAnchorElement(evt.currentTarget)

  const svg = useMemo(() => {
    if (circle) {
      return `
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="49" fill="${Colors.customAreaFill}" stroke="${Colors.customAreaStroke}" stroke-width="2" />
        </svg>
      `
    }

    if (!poly) {
      return null
    }
    const geojson: Feature<Polygon> = {
      type: 'Feature',
      geometry: poly,
      properties: {
        type: 'Polygon',
        fill: Colors.customAreaFill,
        stroke: Colors.customAreaStroke,
        'stroke-width': '2',
      },
    }
    const VectorSquare = bbox(geojson)

    // Configure the converter with size/bbox
    const converter = new GeoJSON2SVG({
      viewportSize: {
        width: 180,
        height: 180,
      },
      mapExtent: {
        left: VectorSquare[0],
        right: VectorSquare[2],
        top: VectorSquare[3],
        bottom: VectorSquare[1],
      },
    })
    // Convert GeoJSON to SVG with styles
    const svg = converter.convert(geojson, {
      // The type definition don't match the documentation
      // @ts-ignore
      attributes: ['properties.fill', 'properties.stroke', 'properties.stroke-width'],
    })

    const svgStr = `<svg viewBox="0 0 180 180" xmlns="http://www.w3.org/2000/svg">${svg}</svg>`
    return svgStr
  }, [circle, poly])

  return (
    <Wrapper>
      <Cover>
        {svg && <CustomAreaContainer dangerouslySetInnerHTML={{ __html: svg }} />}
        <CloseButton onClick={onClose}>
          <Close size={16} />
        </CloseButton>
      </Cover>
      <StyledSaveButtonWrapper>
        <SaveButton
          color={isSaved ? Colors.primaryGreen400 : Colors.primaryBlue400}
          inverted={isSaved}
          onClick={openUserListsPopover}>
          {isSaved ? (
            <CheckCircle size={14} color={Colors.bgWhite} />
          ) : (
            <Save size={14} color={Colors.primaryBlue400} />
          )}
          {`${isSaved ? 'Saved' : 'Save'}`}
          {savedIn.length > 0 && ` (${savedIn.length})`}
        </SaveButton>
        <UserListsPopover />
      </StyledSaveButtonWrapper>
    </Wrapper>
  )
}
